import { useCallback, useEffect, useRef, useState } from 'react';

export const FLASH_UPDATE_ANIMATION_NAME = 'flashUpdate';
export const FLASH_UPDATE_CLASS = 'animate-flashUpdate';
// Clears a flash whose animationend never came (element hidden mid-fade: React
// 18 has no onAnimationCancel), so the next change can still play it. Longer
// than the 1 s fade.
const FLASH_FALLBACK_MS = 2000;

// Arrays and objects compare by content: a list reloaded with the same data
// builds new arrays that must not count as a change.
const toComparable = (signature) =>
  signature !== null && typeof signature === 'object'
    ? JSON.stringify(signature)
    : signature;

/**
 * Lights an item up briefly when `signature` (its mutable fields, or its
 * `updatedAt`) changes after mount, on an element that stays mounted.
 *
 * `identity` is the item's id: a list keyed by index hands a row another item
 * when it reloads, which is not this item changing.
 *
 * Put `onAnimationEnd` on the element carrying `flashClassName`, or on any of
 * its ancestors: the end is recognised by the animation name, so the class may
 * sit on its cells.
 */
const useFlashOnChange = (
  signature,
  { identity, className = FLASH_UPDATE_CLASS } = {},
) => {
  const comparable = toComparable(signature);
  // The previous value rather than a first-render flag: StrictMode runs the
  // mount effect twice, and a flag would flash the item on mount.
  const previous = useRef({ identity, comparable });
  const [isFlashing, setIsFlashing] = useState(false);

  useEffect(() => {
    const { identity: previousIdentity, comparable: previousComparable } =
      previous.current;
    previous.current = { identity, comparable };
    if (!Object.is(previousIdentity, identity)) return;
    if (Object.is(previousComparable, comparable)) return;
    setIsFlashing(true);
  }, [identity, comparable]);

  useEffect(() => {
    if (!isFlashing) return undefined;
    const timer = setTimeout(() => setIsFlashing(false), FLASH_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [isFlashing]);

  const onAnimationEnd = useCallback((event) => {
    if (event?.animationName === FLASH_UPDATE_ANIMATION_NAME) {
      setIsFlashing(false);
    }
  }, []);

  return {
    flashClassName: isFlashing ? className : '',
    onAnimationEnd,
  };
};

export default useFlashOnChange;
